// Vibration FFT spectrum simulator. Produces realistic 3-axis spectra for the
// developer-only simulator mode (bluebox machines do not stream FFT arrays to
// the CCO yet — only per-axis RMS scalars). The payload shape matches the
// real data contract in src/types/fft-spectrum.js, so swapping to live data
// later is a drop-in replacement.
//
// Replaces the old "flat line + uniform pulses" fake: here the spectrum has a
// random noise floor plus gaussian peaks at the shaft order frequencies
// (1x/2x/3x) and the blade-pass frequency (BPF), shaped by a fault profile.

import { validateSpectrum } from '../types/fft-spectrum';

// Deterministic PRNG (mulberry32) + FNV-1a seed hash. Both need bitwise ops;
// the repo lints no-bitwise, so they are disabled for this block only (same
// pattern as the alarm bitmask decoder in constants/bluebox.js). Seeding by
// (axis, sampleIndex, profile) keeps a given sample reproducible for tests
// while consecutive samples still differ, so a scrolling history is not frozen.
/* eslint-disable no-bitwise, operator-assignment */
const mulberry32 = (seed) => {
  let a = seed >>> 0;
  return () => {
    a |= 0;
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

const hashSeed = (axis, sampleIndex, profile) => {
  const str = `${axis}:${sampleIndex}:${profile}`;
  let h = 2166136261;
  for (let i = 0; i < str.length; i += 1) {
    h ^= str.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
};
/* eslint-enable no-bitwise, operator-assignment */

// Fault profiles. Multipliers are applied to the base 1x amplitude. Order
// amplitudes decrease for a healthy machine; each fault reshapes them the way
// the Higra diagnostic guide describes (see BlueBox folder, "Da falha a
// protecao").
export const FFT_PROFILES = {
  healthy: { x1: 1.0, x2: 0.4, x3: 0.15, bpf: 0.3, floorPct: 0.01 },
  // Unbalance: dominant 1x.
  unbalance: { x1: 2.4, x2: 0.3, x3: 0.1, bpf: 0.25, floorPct: 0.01 },
  // Misalignment: 2x near or above 1x, stronger on the axial (z) axis.
  misalignment: {
    x1: 0.9,
    x2: 1.4,
    x3: 0.55,
    bpf: 0.25,
    floorPct: 0.012,
    axialBoost: true,
  },
  // Bearing defect: peaks at non-integer orders (e.g. 3.6x, 5.4x).
  bearing: {
    x1: 1.0,
    x2: 0.35,
    x3: 0.12,
    bpf: 0.3,
    floorPct: 0.015,
    extraOrders: [{ order: 3.6, amp: 0.7 }, { order: 5.4, amp: 0.45 }],
  },
  // Cavitation: raised broadband noise floor above ~1 kHz.
  cavitation: {
    x1: 0.9,
    x2: 0.3,
    x3: 0.1,
    bpf: 0.45,
    floorPct: 0.01,
    broadband: { fromHz: 1000, amp: 0.8 },
  },
};

export const SIMULATOR_FFT_DEFAULTS = {
  sampleRate: 5500,
  binCount: 1024,
  rpm: 3600,
  bladeCount: 5,
  unit: 'mg', // Bluebox accelerometer native unit (milli-g)
  profile: 'healthy',
  // Base amplitude of the 1x peak (in `unit`). Peaks scale off this; the
  // noise floor is a small fraction of it.
  baseAmplitude: 4.0,
};

// Per-axis scaling. Radial axes (x, y) carry the running speed energy; the
// axial axis (z) is generally quieter with a proportionally weaker 1x.
const AXIS_SCALE = {
  x: { overall: 1.0, oneX: 1.0 },
  y: { overall: 0.92, oneX: 0.9 },
  z: { overall: 0.6, oneX: 0.45 },
};

// Add a gaussian bump centered on `centerBin` into the magnitudes array.
// Width 2-3 bins (sigma), spread to ±4 sigma so the tails vanish smoothly.
const addGaussianPeak = (magnitudes, centerBin, amplitude, sigma = 2.5) => {
  const span = Math.ceil(sigma * 4);
  const from = Math.max(0, Math.round(centerBin - span));
  const to = Math.min(magnitudes.length - 1, Math.round(centerBin + span));
  for (let i = from; i <= to; i += 1) {
    const d = i - centerBin;
    magnitudes[i] += amplitude * Math.exp(-(d * d) / (2 * sigma * sigma));
  }
};

/**
 * Generate one FFT spectrum sample for a single axis.
 * @param {Object} [opts]
 * @param {('x'|'y'|'z')} [opts.axis='x']
 * @param {number} [opts.sampleRate]
 * @param {number} [opts.binCount]
 * @param {number} [opts.rpm]
 * @param {number} [opts.bladeCount]
 * @param {string} [opts.unit]
 * @param {string} [opts.profile]
 * @param {number} [opts.baseAmplitude]
 * @param {number} [opts.sampleIndex=0] Advances the history; varies the sample.
 * @param {string|number} [opts.timestamp] ISO string or epoch ms.
 * @returns {import('../types/fft-spectrum').FftSpectrum}
 */
export const generateSpectrum = (opts = {}) => {
  const {
    axis = 'x',
    sampleRate = SIMULATOR_FFT_DEFAULTS.sampleRate,
    binCount = SIMULATOR_FFT_DEFAULTS.binCount,
    rpm = SIMULATOR_FFT_DEFAULTS.rpm,
    bladeCount = SIMULATOR_FFT_DEFAULTS.bladeCount,
    unit = SIMULATOR_FFT_DEFAULTS.unit,
    profile = SIMULATOR_FFT_DEFAULTS.profile,
    baseAmplitude = SIMULATOR_FFT_DEFAULTS.baseAmplitude,
    sampleIndex = 0,
    timestamp,
  } = opts;

  const cfg = FFT_PROFILES[profile] || FFT_PROFILES.healthy;
  const scale = AXIS_SCALE[axis] || AXIS_SCALE.x;
  const binStep = sampleRate / 2 / binCount; // Hz per bin (0..Nyquist)
  const rand = mulberry32(hashSeed(axis, sampleIndex, profile));
  // ±5% amplitude jitter shared by this whole sample so it breathes vs. the
  // previous one without each bin flickering independently.
  const sampleJitter = 1 + (rand() * 2 - 1) * 0.05;

  const freqToBin = (hz) => hz / binStep;
  const oneXHz = rpm / 60;
  const A1 = baseAmplitude * scale.overall;

  const magnitudes = new Array(binCount).fill(0);

  // 1) Noise floor: low random value per bin (0.5-2% of 1x), different every
  //    bin and every sample.
  const floorBase = A1 * cfg.floorPct;
  for (let i = 1; i < binCount; i += 1) {
    magnitudes[i] = floorBase * (0.5 + rand() * 1.5);
  }

  // 2) Broadband elevation above a cutoff (cavitation).
  if (cfg.broadband) {
    const fromBin = Math.max(1, Math.round(freqToBin(cfg.broadband.fromHz)));
    const bbAmp = A1 * cfg.broadband.amp * cfg.floorPct * 8;
    for (let i = fromBin; i < binCount; i += 1) {
      magnitudes[i] += bbAmp * (0.4 + rand());
    }
  }

  // 3) Order peaks: 1x/2x/3x. The 1x gets the per-axis oneX weighting on top
  //    of the overall scale (axial 1x is proportionally weaker).
  const orderAmps = [
    { n: 1, amp: A1 * cfg.x1 * scale.oneX },
    { n: 2, amp: A1 * cfg.x2 },
    { n: 3, amp: A1 * cfg.x3 },
  ];
  orderAmps.forEach(({ n, amp }) => {
    let a = amp;
    // Misalignment pushes 1x/2x energy into the axial (z) direction.
    if (cfg.axialBoost && axis === 'z' && n <= 2) a *= 2.0;
    const bin = freqToBin(oneXHz * n);
    if (bin >= 1 && bin < binCount) {
      addGaussianPeak(magnitudes, bin, a * sampleJitter * (0.97 + rand() * 0.06));
    }
  });

  // 4) Blade-pass frequency.
  if (bladeCount > 0) {
    const bpfBin = freqToBin(oneXHz * bladeCount);
    if (bpfBin >= 1 && bpfBin < binCount) {
      addGaussianPeak(magnitudes, bpfBin, A1 * cfg.bpf * sampleJitter);
    }
  }

  // 5) Non-integer order peaks (bearing defects).
  if (cfg.extraOrders) {
    cfg.extraOrders.forEach(({ order, amp }) => {
      const bin = freqToBin(oneXHz * order);
      if (bin >= 1 && bin < binCount) {
        addGaussianPeak(magnitudes, bin, A1 * amp * sampleJitter);
      }
    });
  }

  // DC bin removed at the source (mean already subtracted before the FFT).
  magnitudes[0] = 0;

  return {
    axis,
    sampleRate,
    binCount,
    binStep,
    unit,
    timestamp:
      typeof timestamp === 'string'
        ? timestamp
        : new Date(typeof timestamp === 'number' ? timestamp : Date.now()).toISOString(),
    magnitudes,
    rpm,
    bladeCount,
  };
};

/**
 * Generate all three axes at once (same sample instant / profile).
 * @param {Object} [opts] same as generateSpectrum, minus `axis`.
 * @returns {{ x: FftSpectrum, y: FftSpectrum, z: FftSpectrum }}
 */
export const generateSpectrumSet = (opts = {}) => ({
  x: generateSpectrum({ ...opts, axis: 'x' }),
  y: generateSpectrum({ ...opts, axis: 'y' }),
  z: generateSpectrum({ ...opts, axis: 'z' }),
});

// Guard used by callers/tests that want to fail loudly on a bad generator
// change: generate + validate in one step.
export const generateValidatedSpectrum = (opts = {}) => {
  const spectrum = generateSpectrum(opts);
  const { ok, error } = validateSpectrum(spectrum);
  if (!ok) throw new Error(`generated spectrum invalid: ${error}`);
  return spectrum;
};
